import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantDto } from "@/features/merchant/api/dto";
import type { MerchantKycStatus } from "@/features/shared/api/enums";

/**
 * Met à jour le statut KYC d'un marchand
 * (`POST /Merchants/{id}/kyc-status?kycStatus=...`). Rôles :
 * flashpay-superadmin, flashpay-admin, flashpay-service.
 */
export const useSetMerchantKycStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "merchants", "set-kyc-status"],
    mutationFn: ({
      merchantId,
      kycStatus,
    }: {
      merchantId: string;
      kycStatus: MerchantKycStatus;
    }) =>
      mutateData({
        url: `${merchantEndpoints.merchants.kycStatus(merchantId)}?kycStatus=${kycStatus}`,
        method: "POST",
      }) as Promise<MerchantDto>,
    onSuccess: (_data, { merchantId }) => {
      return invalidateAfterMutation(
        queryClient,
        merchantWriteCacheKeys(merchantId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSetMerchantKycStatus failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
